import { BarChart, Download, Image } from "@reverb-ui/icons-react";
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from "@reverb-ui/react";
import domtoimage from "modules/domToImage";
import { createBlobDownloader } from "utils/files/downloadBlob";
import {
  LastFilterSelected,
  TPanelProps,
  formatedActualDate,
  panelsInfo,
} from "helper/panelsHelper";
import { useContext, useEffect, useState } from "react";
import {
  ReportsFilterContext,
  TFiltersState,
} from "presentation/containers/context-api/ReportsFilterProvider";
import { EDownloadFormat } from "data/constants/constants";
import { SegmentContext } from "presentation/containers/context-api/SegmentProvider";

export type TSelectDownloadOptionProps = {
  filterStateIndex: number;
  onClickGenerateCSV?: () => void;
  downloadingReports?: boolean;
  panelInfo?: TPanelProps;
};

const filterDownload = (node: any) => {
  const exclusionClasses = ["dowloadMenu"];
  return !exclusionClasses.some((classname) =>
    node.classList?.contains(classname)
  );
};

const donwloadFileName = (
  filterState: TFiltersState,
  panelInfo?: TPanelProps
): string => {
  const filterName = LastFilterSelected(filterState);
  if (
    panelInfo?.panelId === panelsInfo.creativeGoal.panelId ||
    panelInfo?.panelId === panelsInfo.pageSpeed.panelId
  ) {
    return `${panelInfo?.description} - ${
      filterName.experienceName
    } - ${formatedActualDate()}`;
  }

  const deviceName = filterName.deviceName.toLowerCase();
  return `${panelInfo?.description} - ${filterName.experienceName} - ${filterName.rangeName} on ${deviceName}`;
};

export const SelectDownloadOption = ({
  filterStateIndex,
  onClickGenerateCSV,
  downloadingReports,
  panelInfo,
}: TSelectDownloadOptionProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { filterState } = useContext(ReportsFilterContext);
  const { segmentDownloadEvent } = useContext(SegmentContext);
  const currentFilterState = filterState[filterStateIndex];

  useEffect(() => {
    setIsLoading(downloadingReports || false);
  }, [downloadingReports]);

  const csvDonwload = () => {
    if (onClickGenerateCSV) {
      setIsLoading(true);
      onClickGenerateCSV();
    }
  };

  const pngDownload = () => {
    setIsLoading(true);
    const node = document.getElementById(panelInfo?.htmlId || "")!;
    const scaleFactor = 3;

    const param = {
      scale: scaleFactor,
      removeHeightClass: panelInfo?.downloadOptions?.classToOverflow || [],
      filter: filterDownload,
      style: {
        transform: `scale(${scaleFactor})`,
        transformOrigin: "top left",
      },
    };

    domtoimage
      .toBlob(node, param)
      .then(
        createBlobDownloader(
          `${donwloadFileName(currentFilterState, panelInfo)}.png`
        )
      )
      .then(() => setIsLoading(false))
      .then(() => sendSegmentDownloadEvent(EDownloadFormat.PNG));
  };

  const sendSegmentDownloadEvent = (downloadFormat: EDownloadFormat) => {
    return segmentDownloadEvent({
      format: downloadFormat.toString(),
      panelType: panelInfo?.panelId!,
    });
  };

  return (
    <Box className="dowloadMenu">
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label="Download report"
          icon={
            isLoading ? (
              <Spinner color="blue.500" size="sm" />
            ) : (
              <Download color="blue.600" />
            )
          }
        />
        <MenuList p={0} minW={"98px"}>
          {onClickGenerateCSV && (
            <MenuItem onClick={csvDonwload}>
              <BarChart color="neutral.500" />
              <Text color={"neutral.800"} size={"14px"} px={"1.5"}>
                CSV
              </Text>
            </MenuItem>
          )}
          {panelInfo && (
            <MenuItem
              onClick={() => {
                pngDownload();
              }}
            >
              <Image color="neutral.500" />
              <Text color={"neutral.800"} size={"14px"} px={"1.5"}>
                PNG
              </Text>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};
